"use client";

import Cookies from "js-cookie";
import { create } from "zustand";

import { parseReferral } from "lib/api/pro-api/pricingPlansPath";
import { ProAPI } from "lib/api/pro-api/pro-api";
import { analyticsStore } from "store/analyticsStore";

import {
  INFLUENCER_REFERRAL_ARRIVAL_EVENT,
  INFLUENCER_REFERRAL_COOKIE,
  influencerReferralArrivalAnalytics,
  parseInfluencerReferralReceipt,
  type InfluencerReferralReceipt,
} from "./shared";

type ReferralStatus = "idle" | "resolving" | "resolved" | "failed";

interface InfluencerReferralState {
  status: ReferralStatus;
  requestedHandle: string | null;
  receipt: InfluencerReferralReceipt | null;
}

const initialState: InfluencerReferralState = {
  status: "idle",
  requestedHandle: null,
  receipt: null,
};

export const influencerReferralStore = create<InfluencerReferralState>(
  () => initialState,
);

function cookieAttributes() {
  const productionDomain =
    window.location.hostname === "invideo.io" ||
    window.location.hostname === "www.invideo.io";
  return {
    path: "/",
    sameSite: "lax" as const,
    secure: window.location.protocol === "https:",
    ...(productionDomain ? { domain: ".invideo.io" } : {}),
  };
}

function removeReceipt() {
  Cookies.remove(INFLUENCER_REFERRAL_COOKIE, { path: "/" });
  if (
    window.location.hostname === "invideo.io" ||
    window.location.hostname === "www.invideo.io"
  ) {
    Cookies.remove(INFLUENCER_REFERRAL_COOKIE, {
      path: "/",
      domain: ".invideo.io",
    });
  }
}

function persistReceipt(receipt: InfluencerReferralReceipt) {
  removeReceipt();
  Cookies.set(INFLUENCER_REFERRAL_COOKIE, JSON.stringify(receipt), {
    ...cookieAttributes(),
    expires: new Date(receipt.expires_at),
  });
}

function storedReceipt() {
  const receipt = parseInfluencerReferralReceipt(
    Cookies.get(INFLUENCER_REFERRAL_COOKIE),
  );
  if (!receipt) removeReceipt();
  return receipt;
}

export function hasPersistedInfluencerReferral(
  expected: InfluencerReferralReceipt,
) {
  return storedReceipt()?.token === expected.token;
}

function trackReferralArrival(receipt: InfluencerReferralReceipt) {
  try {
    analyticsStore.getState().trackEvent({
      eventName: INFLUENCER_REFERRAL_ARRIVAL_EVENT,
      eventData: influencerReferralArrivalAnalytics(receipt),
    });
  } catch (error) {
    console.error("Failed to track influencer referral arrival:", error);
  }
}

let inFlight:
  | { handle: string; promise: Promise<InfluencerReferralReceipt | null> }
  | undefined;

function resolveHandle(
  handle: string,
  previousReceipt: InfluencerReferralReceipt | null,
): Promise<InfluencerReferralReceipt | null> {
  if (inFlight?.handle === handle) return inFlight.promise;

  influencerReferralStore.setState({
    status: "resolving",
    requestedHandle: handle,
    receipt: previousReceipt,
  });

  const promise: Promise<InfluencerReferralReceipt | null> =
    ProAPI.influencerCampaign(handle)
      .then((response) => {
        const receipt = parseInfluencerReferralReceipt(
          JSON.stringify(response.data.ok?.referral),
        );
        if (!receipt || receipt.handle.toLowerCase() !== handle.toLowerCase()) {
          throw new Error("Invalid influencer referral receipt");
        }

        persistReceipt(receipt);
        influencerReferralStore.setState({
          status: "resolved",
          requestedHandle: handle,
          receipt,
        });
        trackReferralArrival(receipt);
        return receipt;
      })
      .catch(() => {
        influencerReferralStore.setState({
          status: "failed",
          requestedHandle: handle,
          receipt: previousReceipt,
        });
        return previousReceipt;
      })
      .finally(() => {
        if (inFlight?.promise === promise) inFlight = undefined;
      });

  inFlight = { handle, promise };
  return promise;
}

export function initializeInfluencerReferral(): Promise<InfluencerReferralReceipt | null> {
  if (typeof window === "undefined") return Promise.resolve(null);

  const params = new URLSearchParams(window.location.search);
  const handle =
    params.get("via") === "influencer"
      ? parseReferral(params.get("ref"))
      : null;
  if (handle) {
    const state = influencerReferralStore.getState();
    const current = state.receipt;
    const receipt = current
      ? parseInfluencerReferralReceipt(JSON.stringify(current))
      : storedReceipt();
    if (receipt?.handle.toLowerCase() === handle.toLowerCase()) {
      influencerReferralStore.setState({
        status: "resolved",
        requestedHandle: handle,
        receipt,
      });
      return Promise.resolve(receipt);
    }
    if (state.status === "failed" && state.requestedHandle === handle)
      return Promise.resolve(receipt);
    return resolveHandle(handle, receipt);
  }

  const receipt = storedReceipt();
  influencerReferralStore.setState({
    status: "resolved",
    requestedHandle: null,
    receipt,
  });
  return Promise.resolve(receipt);
}

export function getInfluencerReferralForSignup(): Promise<InfluencerReferralReceipt | null> {
  const state = influencerReferralStore.getState();
  if (state.status === "resolving" && inFlight) {
    const previous = state.receipt
      ? parseInfluencerReferralReceipt(JSON.stringify(state.receipt))
      : null;
    return previous ? Promise.resolve(previous) : inFlight.promise;
  }
  if (state.status === "resolved") {
    const receipt = state.receipt
      ? parseInfluencerReferralReceipt(JSON.stringify(state.receipt))
      : null;
    if (receipt) return Promise.resolve(receipt);
  }
  return initializeInfluencerReferral();
}

export function resetInfluencerReferralForTests() {
  inFlight = undefined;
  influencerReferralStore.setState(initialState, true);
}
